import { useMutation } from "@apollo/client";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CallMerge from "@mui/icons-material/CallMerge";
import ChangeCircle from "@mui/icons-material/ChangeCircle";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import LocalShipping from "@mui/icons-material/LocalShipping";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Alert, Box, Button, IconButton, Stack, SvgIcon, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { StatusChip } from "components/StatusChip";
import { FillEventSource, GetWasteContainerQuery } from "generated-graphql/graphql";
import { useState } from "react";

import { CONTAINER_MUTATION_REFETCH_QUERIES, DELETE_CONTAINER_FILL_EVENT_MUTATION } from "./api";
import {
  FILL_EVENT_SOURCE_LABELS,
  formatAmountCapacity,
  formatContainerDate,
  formatContainerDateLong,
  formatQuantity,
  formatSignedQuantity,
} from "./containerDisplay";
import { ContainerFillEventDialog } from "./ContainerFillEventDialog";
import { EmptySectionState, SectionCard } from "./containerSections";
import { SetContainerAmountDialog } from "./SetContainerAmountDialog";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerFillEvent = WasteContainerDetail["fillEvents"][number];

/** Title, icon, and color for a fill-history row, keyed by what the event is. */
function fillEventVisual(event: ContainerFillEvent): {
  title: string;
  icon: typeof SvgIcon;
  color: string;
} {
  if (event.sourceContainerId) {
    return { title: "Consolidated in", icon: CallMerge, color: "info.main" };
  }
  if (event.source === FillEventSource.ReconciledFill) {
    return { title: "Reconciled to capacity", icon: ChangeCircle, color: "success.main" };
  }
  if (event.source === FillEventSource.Shipment) {
    // The note carries the request number, so the row already names the
    // shipment underneath this title.
    return { title: "Shipped", icon: LocalShipping, color: "warning.main" };
  }
  if (event.amount < 0) {
    return { title: "Waste removed", icon: RemoveCircleOutline, color: "warning.main" };
  }
  return { title: "Waste added", icon: AddCircleOutline, color: "success.main" };
}

function FillEventRow({
  event,
  container,
  runningTotal,
  showConnector,
  readOnly,
  onEdit,
  onDelete,
}: {
  event: ContainerFillEvent;
  container: WasteContainerDetail;
  runningTotal: number | null | undefined;
  showConnector: boolean;
  readOnly: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { title, icon: Icon, color } = fillEventVisual(event);
  const isRemoval = event.amount < 0;
  // A shipment owns its removal: editing it here would put the waste back on
  // a container whose shipment still reports it gone, so the server refuses.
  const ownedByShipment = event.source === FillEventSource.Shipment;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="stretch"
      sx={{ "&:hover .fill-event-actions": { opacity: 1 } }}
    >
      {/* Timeline rail: status icon over a connector line to the next entry. */}
      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
        <Icon sx={{ color, fontSize: 24 }} />
        {showConnector && <Box sx={{ flex: 1, width: "1px", bgcolor: "grey.300", mt: 0.5 }} />}
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0, pb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">{title}</Typography>
              {event.isEstimate && (
                <Typography variant="caption" color="text.secondary">
                  (estimate)
                </Typography>
              )}
              {event.source !== FillEventSource.Manual &&
                event.source !== FillEventSource.ReconciledFill && (
                  <StatusChip
                    color="default"
                    variant="soft"
                    label={FILL_EVENT_SOURCE_LABELS[event.source]}
                  />
                )}
            </Stack>
            {event.note && (
              <Typography variant="caption" color="text.secondary" display="block">
                {event.note}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {formatContainerDateLong(event.occurredAt)}
            </Typography>
          </Box>

          <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500 }}
              color={isRemoval ? "warning.main" : "success.main"}
            >
              {formatSignedQuantity(event.amount, event.unit)}
            </Typography>
            {runningTotal != null && (
              <Typography variant="caption" color="text.secondary">
                {formatAmountCapacity({
                  amount: runningTotal,
                  amountUnit: container.amountUnit,
                  capacity: container.capacity,
                  capacityUnit: container.capacityUnit,
                })}
              </Typography>
            )}
            {!readOnly && !ownedByShipment && (
              <Stack
                direction="row"
                spacing={0.5}
                className="fill-event-actions"
                sx={{ opacity: 0, transition: "opacity 0.15s" }}
              >
                <IconButton size="small" aria-label="Edit fill event" onClick={onEdit}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Delete fill event" onClick={onDelete}>
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}

/** What the confirm dialog spells out before a fill event is deleted. */
function deleteConfirmation(event: ContainerFillEvent | null): string {
  if (!event) {
    return "";
  }
  return `${formatSignedQuantity(event.amount, event.unit)} on ${formatContainerDate(
    event.occurredAt
  )} will be removed and the container's total will recompute.`;
}

/**
 * The events themselves, newest first, under the running total they add up to.
 *
 * Split from the section around it so that component stays a description of
 * the card — its heading, its controls, the dialogs it can open — rather than
 * also being the list.
 */
function FillHistoryBody({
  container,
  readOnly,
  onEdit,
  onDelete,
}: {
  container: WasteContainerDetail;
  readOnly: boolean;
  onEdit: (event: ContainerFillEvent) => void;
  onDelete: (event: ContainerFillEvent) => void;
}) {
  const fillEvents = container.fillEvents;
  if (fillEvents.length === 0) {
    return (
      <EmptySectionState
        message={`No fill events recorded yet${
          readOnly ? "." : " — log the first add or remove above."
        }`}
      />
    );
  }
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current total: {formatQuantity(container.amount, container.amountUnit)}
      </Typography>
      <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
        {fillEvents.map((event, index) => (
          <FillEventRow
            key={event.id}
            event={event}
            container={container}
            runningTotal={event.runningTotal}
            showConnector={index < fillEvents.length - 1}
            readOnly={readOnly}
            onEdit={() => onEdit(event)}
            onDelete={() => onDelete(event)}
          />
        ))}
      </Box>
    </>
  );
}

export function FillHistorySection({ container }: { container: WasteContainerDetail }) {
  const alerts = useAlerts();
  const readOnly = !!container.archivedAt;
  const fillEvents = container.fillEvents;

  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [setAmountDialogOpen, setSetAmountDialogOpen] = useState(false);
  const [eventBeingEdited, setEventBeingEdited] = useState<ContainerFillEvent | null>(null);
  const [eventBeingDeleted, setEventBeingDeleted] = useState<ContainerFillEvent | null>(null);

  const [deleteFillEvent, { loading: isDeleting }] = useMutation(
    DELETE_CONTAINER_FILL_EVENT_MUTATION,
    {
      refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
    }
  );

  const onConfirmDelete = async () => {
    if (!eventBeingDeleted) {
      return;
    }
    await deleteFillEvent({
      variables: { id: eventBeingDeleted.id },
      onCompleted: () => {
        alerts.success("Fill event deleted");
        setEventBeingDeleted(null);
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while deleting the fill event");
        setEventBeingDeleted(null);
      },
    });
  };

  const totalUnavailable = container.amount == null && fillEvents.length > 0;

  return (
    <SectionCard
      title="Fill history"
      actions={
        !readOnly && (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setSetAmountDialogOpen(true)}>
              Set amount
            </Button>
            {/* The one bordered button on the page, and no plus on it: this
                logs removals as readily as additions, so a "+" names half of
                what it does. */}
            <Button size="small" variant="outlined" onClick={() => setLogDialogOpen(true)}>
              Log fill event
            </Button>
          </Stack>
        )
      }
    >
      {totalUnavailable && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          The recorded amounts span units that can't be combined, so the running total is
          unavailable. Edit the fill events to a consistent unit to restore it.
        </Alert>
      )}

      <FillHistoryBody
        container={container}
        readOnly={readOnly}
        onEdit={setEventBeingEdited}
        onDelete={setEventBeingDeleted}
      />

      {logDialogOpen && (
        <ContainerFillEventDialog container={container} onClose={() => setLogDialogOpen(false)} />
      )}
      {eventBeingEdited && (
        <ContainerFillEventDialog
          container={container}
          fillEvent={eventBeingEdited}
          onClose={() => setEventBeingEdited(null)}
        />
      )}
      {setAmountDialogOpen && (
        <SetContainerAmountDialog
          container={container}
          onClose={() => setSetAmountDialogOpen(false)}
        />
      )}
      <ConfirmDialog
        open={!!eventBeingDeleted}
        loading={isDeleting}
        header="Delete fill event?"
        info={deleteConfirmation(eventBeingDeleted)}
        onConfirm={onConfirmDelete}
        onCancel={() => setEventBeingDeleted(null)}
        onClose={() => setEventBeingDeleted(null)}
      />
    </SectionCard>
  );
}
